import { LayerGroup, LayerSection } from 'features/mapping/mapping.types';
import { useQueryParams } from 'hooks/useQueryParams';
import { useCallback } from 'react';
import { useSessionStorage } from 'usehooks-ts';

export type LayersDrawerContentType = 'layers' | 'legend';

export type LayersDrawerParams = {
  menu?: LayersDrawerContentType;
  section?: LayerSection;
  group?: LayerGroup;
};

export const LAYERS_DRAWER_PARAM_KEYS: readonly (keyof LayersDrawerParams)[] = [
  'menu',
  'section',
  'group',
];

export type UseLayersDrawerState = {
  open: boolean;
  menu?: LayersDrawerContentType;
  section?: LayerSection;
  group?: LayerGroup;
  openLayersDrawer: (menu: LayersDrawerContentType) => void;
  closeLayersDrawer: () => void;
  openLayersSection: (section: LayerSection) => void;
  openLayersGroup: (group: LayerGroup) => void;
  closeLayersGroup: () => void;
};

export const SELECTED_LAYERS_SECTION_STORAGE_KEY = 'SELECTED_LAYERS_SECTION';

export const DEFAULT_SELECTED_SECTION: LayerSection = 'general';

const LAYER_MENUS: LayersDrawerContentType[] = ['layers', 'legend'];

const LAYER_SECTIONS: LayerSection[] = [
  'general',
  'weather',
  'flood',
  'professional',
  'team',
];

const LAYER_GROUPS: LayerGroup[] = [
  'firePerimeters',
  'satelliteHotspots',
  'federalLands',
  'criticalInfrastructure',
];

export const useLayersDrawerState = (): UseLayersDrawerState => {
  // We want to store the selected section in session storage
  // so that closing and reopening the drawer restores the last section.
  const [selectedSection, setSelectedSection] = useSessionStorage<
    LayerSection | undefined
  >(SELECTED_LAYERS_SECTION_STORAGE_KEY, DEFAULT_SELECTED_SECTION);
  const [queryParams, setQueryParams] = useQueryParams<LayersDrawerParams>();

  const menu =
    queryParams.menu && LAYER_MENUS.includes(queryParams.menu)
      ? queryParams.menu
      : undefined;

  // eslint-disable-next-line no-nested-ternary
  const section = queryParams.section
    ? LAYER_SECTIONS.includes(queryParams.section)
      ? queryParams.section
      : selectedSection
    : selectedSection;

  const group =
    queryParams.group && LAYER_GROUPS.includes(queryParams.group)
      ? queryParams.group
      : undefined;

  const open = Boolean(menu);

  const openLayersDrawer = useCallback(
    (drawerMenu: LayersDrawerContentType): void => {
      setQueryParams({
        ...queryParams,
        menu: drawerMenu,
        section: undefined,
        group: undefined,
      });
    },
    [queryParams, setQueryParams],
  );

  const closeLayersDrawer = useCallback((): void => {
    setQueryParams({
      ...queryParams,
      menu: undefined,
      section: undefined,
      group: undefined,
    });
  }, [queryParams, setQueryParams]);

  const openLayersSection = useCallback(
    (drawerSection: LayerSection): void => {
      const newSection =
        drawerSection === selectedSection ? undefined : drawerSection;
      setQueryParams({
        ...queryParams,
        section: newSection,
        group: undefined,
      });
      setSelectedSection(newSection);
    },
    [queryParams, selectedSection, setQueryParams, setSelectedSection],
  );

  const openLayersGroup = useCallback(
    (drawerGroup: LayerGroup): void => {
      setQueryParams({ ...queryParams, group: drawerGroup });
    },
    [queryParams, setQueryParams],
  );

  const closeLayersGroup = useCallback((): void => {
    setQueryParams({ ...queryParams, group: undefined });
  }, [queryParams, setQueryParams]);

  return {
    open,
    menu,
    section,
    group,
    openLayersDrawer,
    closeLayersDrawer,
    openLayersSection,
    openLayersGroup,
    closeLayersGroup,
  };
};
